/**
 * Karten-Schema (Frontend-Spiegel von core/backend/src/agents/kartenSchema.ts).
 * Handgeschriebene Typ-Guards, kein zod im Frontend. Karteninhalt wird nie
 * vertraut: sichereKarte() wirft nie und liefert im Zweifel eine Fehlerkarte.
 *
 * WICHTIG: Reines TypeScript ohne Imports — der Backend-Test kartenSynchron.test.ts
 * importiert diese Datei direkt. Keine Abhaengigkeit auf React, Aliase oder andere Dateien.
 * Das Modell uebergibt nie Farben: Serien tragen nur `rolle` (+ `schluessel`).
 */

export type Quelle = string;
export type Ton = 'info' | 'warnung' | 'erfolg';
export type WertFormat = 'number' | 'euro' | 'percent';
export type SpaltenFormat = WertFormat | 'text' | 'delta' | 'balken';
export type VorschlagStatus = 'offen' | 'angenommen' | 'abgelehnt' | 'stumm' | 'erledigt';
export type Richtung = 'hoch' | 'runter' | 'gleich';
export type Gut = 'hoch' | 'runter';
export type SerienRolle = 'primaer' | 'sekundaer' | 'vorjahr' | 'prognose' | 'spanne' | 'ziel' | 'neutral';
export type Schwere = 'rot' | 'gelb' | 'gruen';
export type Ampel = 'gruen' | 'gelb' | 'rot';

/** Spiegel von agentConfig.KARTEN_LIMITS. */
export const LIMITS = {
  leiste: 5,
  kennzahlSparkline: 24,
  linieLabels: 36,
  linieSerien: 5,
  saeulenLabels: 24,
  saeulenSerien: 4,
  ringSegmente: 6,
  ringListe: 8,
  alarmliste: 20,
  tabelleZeilen: 50,
  balken: 30,
  textZeichen: 1500,
  umschalter: 4,
  titel: 120,
  untertitel: 160,
} as const;

/** Spiegel von agentConfig.KARTEN_ICONS. */
export const ICONS = [
  'Wallet', 'ShoppingCart', 'Package', 'Percent', 'Clock', 'AlertTriangle', 'TrendingUp',
  'Users', 'Truck', 'Mail', 'Euro', 'Gauge', 'FileText', 'Boxes',
] as const;
export type KartenIcon = (typeof ICONS)[number];

/** Spiegel von agentConfig.KARTEN_ZONEN (Reihenfolge = Anzeige von oben nach unten). */
export const ZONEN = ['leiste', 'kennzahlen', 'haupt', 'seite', 'fuss'] as const;
export type Zone = (typeof ZONEN)[number];

/** Spiegel von kartenSchema.KARTEN_TYPEN. */
export const KARTEN_TYPEN = [
  'hinweis', 'vorschlag', 'kennzahl', 'tabelle', 'liste', 'balken',
  'ring', 'linie', 'saeulen', 'alarmliste', 'fortschritt', 'text',
] as const;
export type KartenTyp = (typeof KARTEN_TYPEN)[number];

/** Alte Namen bleiben fuer bestehende Verbraucher. */
export const MAX_BALKEN = LIMITS.balken;
export const MAX_TABELLEN_ZEILEN = LIMITS.tabelleZeilen;

export interface KartenWarum {
  runId: string | number;
  werkzeug: string;
}

export interface KartenUmschlag {
  id: string;
  erzeugtAm: string;
  quelle: Quelle;
  quelleName?: string;
  untertitel?: string;
  stand?: string;
  warum?: KartenWarum;
  stummBis?: string;
  beispiel?: boolean;
}

export interface HinweisKarte extends KartenUmschlag {
  typ: 'hinweis';
  titel: string;
  text: string;
  ton: Ton;
}

export interface VorschlagAktion {
  label: string;
  route: string;
  params?: Record<string, string>;
}

export interface VorschlagKarte extends KartenUmschlag {
  typ: 'vorschlag';
  titel: string;
  begruendung: string;
  aktion?: VorschlagAktion;
  status: VorschlagStatus;
}

export interface Vergleich {
  wert: number;
  label: string;
  richtung?: Richtung;
  gut?: Gut;
}

export interface DuoEintrag {
  label: string;
  wert: number | string;
  sub?: string;
  schluessel?: string;
}

export interface KennzahlKarte extends KartenUmschlag {
  typ: 'kennzahl';
  titel: string;
  wert: number | string;
  einheit?: string;
  format?: WertFormat;
  trend?: { richtung: Richtung; text: string };
  sparkline?: number[];
  vergleich?: Vergleich;
  ziel?: { wert: number; label?: string };
  ampel?: Ampel;
  duo?: [DuoEintrag, DuoEintrag];
  icon?: KartenIcon;
}

export interface TabellenSpalte {
  key: string;
  label: string;
  format?: SpaltenFormat;
  gut?: Gut;
}

export interface TabelleKarte extends KartenUmschlag {
  typ: 'tabelle';
  titel: string;
  spalten: TabellenSpalte[];
  zeilen: Array<Record<string, unknown>>;
  fusszeile?: Record<string, unknown>;
  sortierbar?: boolean;
  hervorheben?: number[];
}

export interface ListeKarte extends KartenUmschlag {
  typ: 'liste';
  titel: string;
  eintraege: Array<{ text: string; link?: string }>;
}

export interface BalkenEintrag {
  label: string;
  wert: number;
  schluessel?: string;
  rolle?: SerienRolle;
  zielProzent?: number;
  ziel?: number;
  label2?: string;
}

export interface BalkenKarte extends KartenUmschlag {
  typ: 'balken';
  titel: string;
  einheit?: string;
  format?: WertFormat;
  prozentLabel?: boolean;
  balken: BalkenEintrag[];
}

export interface Ring {
  label: string;
  wert: number;
  schluessel?: string;
}

export interface RingListeEintrag {
  label: string;
  wert: string;
  ziel?: string;
  schluessel?: string;
}

export interface RingKarte extends KartenUmschlag {
  typ: 'ring';
  titel: string;
  wert: number;
  max?: number;
  zentrum: { wert: string; label: string };
  ziel?: number;
  segmente?: Ring[];
  liste?: RingListeEintrag[];
}

export type Werte = Array<number | null>;

export interface SerienDef {
  label: string;
  werte: Werte;
  rolle: SerienRolle;
  schluessel?: string;
  gestrichelt?: boolean;
  flaeche?: boolean;
}

export interface LinieKarte extends KartenUmschlag {
  typ: 'linie';
  titel: string;
  labels: string[];
  serien: SerienDef[];
  spanne?: { unten: Werte; oben: Werte };
  zielwert?: number;
  einheit?: string;
  format?: WertFormat;
  umschalter?: Array<{ label: string; serienIndex: number[] }>;
}

export interface SaeulenKarte extends KartenUmschlag {
  typ: 'saeulen';
  titel: string;
  labels: string[];
  serien: SerienDef[];
  gestapelt?: boolean;
  ziellinie?: Werte;
  einheit?: string;
  format?: WertFormat;
}

export interface AlarmEintrag {
  schwere: Schwere;
  text: string;
  detail?: string;
  zeit?: string;
  link?: string;
  /** Bearbeitbarer Eintrag (z. B. Wächter: `regel:auftragsnummer`). */
  ref?: string;
}

export interface AlarmlisteKarte extends KartenUmschlag {
  typ: 'alarmliste';
  titel: string;
  eintraege: AlarmEintrag[];
}

export interface FortschrittKarte extends KartenUmschlag {
  typ: 'fortschritt';
  titel: string;
  wert: number;
  ziel: number;
  einheit?: string;
  format?: WertFormat;
  soll?: number;
  text?: string;
  vergleich?: Vergleich;
}

export interface TextKarte extends KartenUmschlag {
  typ: 'text';
  titel: string;
  text: string;
}

export type Karte =
  | HinweisKarte | VorschlagKarte | KennzahlKarte | TabelleKarte | ListeKarte | BalkenKarte
  | RingKarte | LinieKarte | SaeulenKarte | AlarmlisteKarte | FortschrittKarte | TextKarte;

const TOENE: readonly string[] = ['info', 'warnung', 'erfolg'];
const STATI: readonly string[] = ['offen', 'angenommen', 'abgelehnt', 'stumm', 'erledigt'];
const WERT_FORMATE: readonly string[] = ['number', 'euro', 'percent'];
const SPALTEN_FORMATE: readonly string[] = ['number', 'euro', 'percent', 'text', 'delta', 'balken'];
const RICHTUNGEN: readonly string[] = ['hoch', 'runter', 'gleich'];
const GUT: readonly string[] = ['hoch', 'runter'];
const ROLLEN: readonly string[] = ['primaer', 'sekundaer', 'vorjahr', 'prognose', 'spanne', 'ziel', 'neutral'];
const SCHWEREN: readonly string[] = ['rot', 'gelb', 'gruen'];
const AMPELN: readonly string[] = ['gruen', 'gelb', 'rot'];
const QUELLE_MAX = 60;
const SCHLUESSEL_MAX = 40;

type Roh = Record<string, unknown>;

function istObjekt(x: unknown): x is Roh {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}
const istString = (x: unknown): x is string => typeof x === 'string';
const istNichtLeer = (x: unknown): x is string => typeof x === 'string' && x.length > 0;
const istOptString = (x: unknown) => x === undefined || typeof x === 'string';
const istZahl = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);
const istOptZahl = (x: unknown) => x === undefined || istZahl(x);
const istOptBool = (x: unknown) => x === undefined || typeof x === 'boolean';
const istStringMax = (x: unknown, max: number): boolean => istString(x) && x.length <= max;
const istOptStringMax = (x: unknown, max: number) => x === undefined || istStringMax(x, max);
const istIn = (x: unknown, liste: readonly string[]): boolean => istString(x) && liste.includes(x);
const istOptIn = (x: unknown, liste: readonly string[]) => x === undefined || istIn(x, liste);
const istWerte = (x: unknown): x is Werte => Array.isArray(x) && x.every((w) => w === null || istZahl(w));
const istTitel = (x: unknown) => istStringMax(x, LIMITS.titel);
const istOptSchluessel = (x: unknown) => istOptStringMax(x, SCHLUESSEL_MAX);

function warumOk(w: unknown): boolean {
  if (w === undefined) return true;
  return istObjekt(w) && (istString(w.runId) || istZahl(w.runId)) && istString(w.werkzeug);
}

function umschlagOk(x: Roh): boolean {
  return (
    istNichtLeer(x.id) &&
    istNichtLeer(x.erzeugtAm) &&
    istNichtLeer(x.quelle) && x.quelle.length <= QUELLE_MAX &&
    istOptStringMax(x.quelleName, QUELLE_MAX) &&
    istOptStringMax(x.untertitel, LIMITS.untertitel) &&
    istOptString(x.stand) &&
    warumOk(x.warum) &&
    istOptString(x.stummBis) &&
    istOptBool(x.beispiel)
  );
}

function vergleichOk(v: unknown): boolean {
  if (v === undefined) return true;
  return istObjekt(v) && istZahl(v.wert) && istString(v.label) && istOptIn(v.richtung, RICHTUNGEN) && istOptIn(v.gut, GUT);
}

function serieOk(s: unknown): boolean {
  return (
    istObjekt(s) && istString(s.label) && istWerte(s.werte) && istIn(s.rolle, ROLLEN) &&
    istOptSchluessel(s.schluessel) && istOptBool(s.gestrichelt) && istOptBool(s.flaeche)
  );
}

function serienOk(serien: unknown, labels: unknown[], max: number): boolean {
  if (!Array.isArray(serien) || serien.length < 1 || serien.length > max) return false;
  return serien.every((s) => serieOk(s) && (s as Roh).werte && ((s as Roh).werte as unknown[]).length === labels.length);
}

export function istHinweisKarte(x: unknown): x is HinweisKarte {
  return (
    istObjekt(x) && x.typ === 'hinweis' && umschlagOk(x) &&
    istString(x.titel) && istString(x.text) && istIn(x.ton, TOENE)
  );
}

export function istVorschlagKarte(x: unknown): x is VorschlagKarte {
  if (!istObjekt(x) || x.typ !== 'vorschlag' || !umschlagOk(x)) return false;
  if (!istString(x.titel) || !istString(x.begruendung)) return false;
  if (!istIn(x.status, STATI)) return false;
  if (x.aktion !== undefined) {
    const a = x.aktion;
    if (!istObjekt(a) || !istString(a.label) || !istString(a.route)) return false;
    if (a.params !== undefined) {
      if (!istObjekt(a.params) || !Object.values(a.params).every(istString)) return false;
    }
  }
  return true;
}

function duoEintragOk(d: unknown): boolean {
  return (
    istObjekt(d) && istString(d.label) && (istZahl(d.wert) || istString(d.wert)) &&
    istOptString(d.sub) && istOptSchluessel(d.schluessel)
  );
}

export function istKennzahlKarte(x: unknown): x is KennzahlKarte {
  if (!istObjekt(x) || x.typ !== 'kennzahl' || !umschlagOk(x)) return false;
  if (!istTitel(x.titel)) return false;
  if (!(istZahl(x.wert) || istString(x.wert))) return false;
  if (!istOptString(x.einheit) || !istOptIn(x.format, WERT_FORMATE)) return false;
  if (x.trend !== undefined) {
    const t = x.trend;
    if (!istObjekt(t) || !istIn(t.richtung, RICHTUNGEN) || !istString(t.text)) return false;
  }
  if (x.sparkline !== undefined) {
    if (!Array.isArray(x.sparkline) || x.sparkline.length > LIMITS.kennzahlSparkline || !x.sparkline.every(istZahl)) return false;
  }
  if (!vergleichOk(x.vergleich)) return false;
  if (x.ziel !== undefined) {
    if (!istObjekt(x.ziel) || !istZahl(x.ziel.wert) || !istOptString(x.ziel.label)) return false;
  }
  if (!istOptIn(x.ampel, AMPELN)) return false;
  if (x.duo !== undefined) {
    if (!Array.isArray(x.duo) || x.duo.length !== 2 || !x.duo.every(duoEintragOk)) return false;
  }
  return istOptIn(x.icon, ICONS);
}

export function istTabelleKarte(x: unknown): x is TabelleKarte {
  if (!istObjekt(x) || x.typ !== 'tabelle' || !umschlagOk(x)) return false;
  if (!istTitel(x.titel)) return false;
  if (!Array.isArray(x.spalten) || !Array.isArray(x.zeilen)) return false;
  if (x.zeilen.length > LIMITS.tabelleZeilen) return false;
  const spaltenOk = x.spalten.every(
    (s) => istObjekt(s) && istString(s.key) && istString(s.label) && istOptIn(s.format, SPALTEN_FORMATE) && istOptIn(s.gut, GUT),
  );
  if (!spaltenOk || !x.zeilen.every(istObjekt)) return false;
  if (x.fusszeile !== undefined && !istObjekt(x.fusszeile)) return false;
  if (!istOptBool(x.sortierbar)) return false;
  if (x.hervorheben !== undefined) {
    const n = x.zeilen.length;
    if (!Array.isArray(x.hervorheben)) return false;
    if (!x.hervorheben.every((i) => typeof i === 'number' && Number.isInteger(i) && i >= 0 && i < n)) return false;
  }
  return true;
}

export function istListeKarte(x: unknown): x is ListeKarte {
  if (!istObjekt(x) || x.typ !== 'liste' || !umschlagOk(x)) return false;
  if (!istTitel(x.titel) || !Array.isArray(x.eintraege)) return false;
  return x.eintraege.every((e) => istObjekt(e) && istString(e.text) && istOptString(e.link));
}

export function istBalkenKarte(x: unknown): x is BalkenKarte {
  if (!istObjekt(x) || x.typ !== 'balken' || !umschlagOk(x)) return false;
  if (!istTitel(x.titel) || !istOptString(x.einheit) || !istOptIn(x.format, WERT_FORMATE) || !istOptBool(x.prozentLabel)) return false;
  if (!Array.isArray(x.balken) || x.balken.length < 1 || x.balken.length > LIMITS.balken) return false;
  return x.balken.every(
    (b) =>
      istObjekt(b) && istString(b.label) && istZahl(b.wert) && istOptSchluessel(b.schluessel) &&
      istOptIn(b.rolle, ROLLEN) && istOptZahl(b.zielProzent) && istOptZahl(b.ziel) && istOptString(b.label2),
  );
}

export function istRingKarte(x: unknown): x is RingKarte {
  if (!istObjekt(x) || x.typ !== 'ring' || !umschlagOk(x)) return false;
  if (!istTitel(x.titel) || !istZahl(x.wert) || !istOptZahl(x.max) || !istOptZahl(x.ziel)) return false;
  if (!istObjekt(x.zentrum) || !istString(x.zentrum.wert) || !istString(x.zentrum.label)) return false;
  if (x.segmente !== undefined) {
    if (!Array.isArray(x.segmente) || x.segmente.length > LIMITS.ringSegmente) return false;
    if (!x.segmente.every((s) => istObjekt(s) && istString(s.label) && istZahl(s.wert) && istOptSchluessel(s.schluessel))) return false;
  }
  if (x.liste !== undefined) {
    if (!Array.isArray(x.liste) || x.liste.length > LIMITS.ringListe) return false;
    if (!x.liste.every((s) => istObjekt(s) && istString(s.label) && istString(s.wert) && istOptString(s.ziel) && istOptSchluessel(s.schluessel))) return false;
  }
  return true;
}

export function istLinieKarte(x: unknown): x is LinieKarte {
  if (!istObjekt(x) || x.typ !== 'linie' || !umschlagOk(x)) return false;
  if (!istTitel(x.titel)) return false;
  if (!Array.isArray(x.labels) || x.labels.length > LIMITS.linieLabels || !x.labels.every(istString)) return false;
  if (!serienOk(x.serien, x.labels, LIMITS.linieSerien)) return false;
  if (x.spanne !== undefined) {
    const s = x.spanne;
    if (!istObjekt(s) || !istWerte(s.unten) || !istWerte(s.oben)) return false;
    if (s.unten.length !== x.labels.length || s.oben.length !== x.labels.length) return false;
  }
  if (!istOptZahl(x.zielwert) || !istOptString(x.einheit) || !istOptIn(x.format, WERT_FORMATE)) return false;
  if (x.umschalter !== undefined) {
    if (!Array.isArray(x.umschalter) || x.umschalter.length > LIMITS.umschalter) return false;
    const ok = x.umschalter.every(
      (u) =>
        istObjekt(u) && istString(u.label) && Array.isArray(u.serienIndex) &&
        u.serienIndex.every((i) => typeof i === 'number' && Number.isInteger(i) && i >= 0),
    );
    if (!ok) return false;
  }
  return true;
}

export function istSaeulenKarte(x: unknown): x is SaeulenKarte {
  if (!istObjekt(x) || x.typ !== 'saeulen' || !umschlagOk(x)) return false;
  if (!istTitel(x.titel)) return false;
  if (!Array.isArray(x.labels) || x.labels.length > LIMITS.saeulenLabels || !x.labels.every(istString)) return false;
  if (!serienOk(x.serien, x.labels, LIMITS.saeulenSerien)) return false;
  if (!istOptBool(x.gestapelt) || !istOptString(x.einheit) || !istOptIn(x.format, WERT_FORMATE)) return false;
  return x.ziellinie === undefined || istWerte(x.ziellinie);
}

export function istAlarmlisteKarte(x: unknown): x is AlarmlisteKarte {
  if (!istObjekt(x) || x.typ !== 'alarmliste' || !umschlagOk(x)) return false;
  if (!istTitel(x.titel) || !Array.isArray(x.eintraege) || x.eintraege.length > LIMITS.alarmliste) return false;
  return x.eintraege.every(
    (e) =>
      istObjekt(e) && istIn(e.schwere, SCHWEREN) && istString(e.text) &&
      istOptString(e.detail) && istOptString(e.zeit) && istOptString(e.link) && istOptString(e.ref),
  );
}

export function istFortschrittKarte(x: unknown): x is FortschrittKarte {
  if (!istObjekt(x) || x.typ !== 'fortschritt' || !umschlagOk(x)) return false;
  return (
    istTitel(x.titel) && istZahl(x.wert) && istZahl(x.ziel) && istOptString(x.einheit) &&
    istOptIn(x.format, WERT_FORMATE) && istOptZahl(x.soll) && istOptString(x.text) && vergleichOk(x.vergleich)
  );
}

export function istTextKarte(x: unknown): x is TextKarte {
  return (
    istObjekt(x) && x.typ === 'text' && umschlagOk(x) && istTitel(x.titel) && istStringMax(x.text, LIMITS.textZeichen)
  );
}

export function istKarte(x: unknown): x is Karte {
  return (
    istHinweisKarte(x) || istVorschlagKarte(x) || istKennzahlKarte(x) ||
    istTabelleKarte(x) || istListeKarte(x) || istBalkenKarte(x) ||
    istRingKarte(x) || istLinieKarte(x) || istSaeulenKarte(x) ||
    istAlarmlisteKarte(x) || istFortschrittKarte(x) || istTextKarte(x)
  );
}

export const FEHLERKARTE_TITEL = 'Kartentyp nicht bekannt';

export function baueFehlerkarte(raw?: unknown): HinweisKarte {
  let id = 'fehler-unbekannt';
  try {
    if (istObjekt(raw) && istNichtLeer(raw.id)) id = `fehler-${raw.id}`;
  } catch {
    /* Getter wirft: Default-ID */
  }
  return {
    id,
    typ: 'hinweis',
    erzeugtAm: new Date().toISOString(),
    quelle: 'system',
    titel: FEHLERKARTE_TITEL,
    text: 'Diese Karte kann nicht angezeigt werden.',
    ton: 'warnung',
  };
}

/** Wirft nie. Ungueltiges wird zur Fehlerkarte. */
export function sichereKarte(raw: unknown): Karte {
  try {
    return istKarte(raw) ? raw : baueFehlerkarte(raw);
  } catch {
    return baueFehlerkarte();
  }
}

/** Badge "Beispiel" haengt nur an beispiel === true, nie an quelle oder Build-Modus. */
export function istBeispiel(karte: unknown): boolean {
  return istObjekt(karte) && karte.beispiel === true;
}
